import React, { useState } from "react";
import * as R from "ramda";
import Papa from "papaparse";
import { useLanguageContext } from "@cadenza/i18n-react";
import {
  LANGUAGE_HANDLING,
  CATEGORY_LABELS,
  ACTIONS,
  defaultTranslations,
  pruneUserTranslations,
  mergeTranslations,
  getCategoryForKey,
  getNativeLabel,
  getUnusedLanguages,
  curatedLanguages,
  isoLanguages,
} from "@cadenza/i18n-core";
import { ICON_COMPONENTS, DEFAULT_ICON } from "@cadenza/icons";
import { CSButton, DeleteButton, CSTextInput } from "@cadenza/ui";

import "./styles/TranslationEditor.css";

const getAvailableLanguages = (translations) =>
  Object.keys(translations.language || {});

const TranslationEditor = () => {
  const {
    STRINGS,
    language,
    setLanguage,
    userTranslations,
    setUserTranslations,
  } = useLanguageContext();

  const L = LANGUAGE_HANDLING;
  const CL = CATEGORY_LABELS;

  const renderIconForKey = (key) => {
    const Icon = ICON_COMPONENTS[key] || DEFAULT_ICON;
    const label = STRINGS[key] || key;
    return <Icon className="cs-icon t-icon" title={label} aria-label={label} />;
  };

  const [editing, setEditing] = useState({});
  const [untranslatedFilter, setUntranslatedFilter] = useState(null);

  const safeUserTranslations = pruneUserTranslations(
    userTranslations,
    defaultTranslations
  );
  const dict = mergeTranslations(defaultTranslations, safeUserTranslations);

  const langs = getAvailableLanguages(dict);
  const langsInUse = Object.keys(dict.language || {});
  const unusedLangs = getUnusedLanguages(langsInUse);

  // Build a Set for quick ISO 639-1 membership checks
  const isoSet = new Set(isoLanguages.map(([code]) => code));

  const [collapsedCategories, setCollapsedCategories] = useState({});
  const toggleCategory = (category) =>
    setCollapsedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));

  const handleChange = (key, lang, value) => {
    const category = getCategoryForKey(key, defaultTranslations);
    setEditing((prev) => R.assocPath([category, key, lang], value, prev));
  };

  const handleSave = () => {
    const merged = R.mergeWith(R.mergeRight, userTranslations || {}, editing);
    setUserTranslations(merged);
    setEditing({});
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(userTranslations, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "riva-translations.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result);
        setUserTranslations(json);
      } catch {
        alert("Invalid JSON file. Please check the file format.");
      }
    };
    reader.readAsText(file);
  };

  const handleExportCSV = () => {
    const safeUser = pruneUserTranslations(
      userTranslations,
      defaultTranslations
    );
    const merged = mergeTranslations(defaultTranslations, safeUser);
    const allKeys = Object.keys(merged._categories || {});
    const allLangs = Object.keys(merged.language || {});
    const rows = [["category", "key", ...allLangs]];

    for (const key of allKeys) {
      const category = merged._categories[key] || "uncategorised";
      const row = [
        category,
        key,
        ...allLangs.map((lang) => merged[key]?.[lang] || ""),
      ];
      rows.push(row);
    }

    const csv = rows
      .map((r) =>
        r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "riva-translations.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => {
        const newTranslations = R.reduce(
          (acc, row) => {
            const category = row.category?.trim();
            const key = row.key?.trim();
            if (!category || !key) return acc;

            const entries = R.toPairs(row).filter(
              ([lang, value]) =>
                lang !== "category" && lang !== "key" && value?.trim()
            );

            return R.reduce(
              (innerAcc, [lang, value]) =>
                R.assocPath([category, key, lang], value.trim(), innerAcc),
              acc,
              entries
            );
          },
          {},
          data
        );

        const merged = R.mergeWith(
          R.mergeRight,
          userTranslations || {},
          newTranslations
        );
        setUserTranslations(merged);
      },
    });
  };

  const [newLangCode, setNewLangCode] = useState("");

  const handleAddLanguage = () => {
    const code = newLangCode.trim().toLowerCase();
    if (!code || langs.includes(code)) return;

    const label = getNativeLabel(code);
    const updated = R.assocPath(
      ["language_handling", "language", code],
      label,
      userTranslations || {}
    );

    setUserTranslations(updated);
    setNewLangCode("");
  };

  const handleRemoveLanguage = (lang) => {
    const removeLanguageRecursively = (obj) =>
      R.mapObjIndexed(
        (val) =>
          typeof val === "object" && val !== null
            ? removeLanguageRecursively(R.omit([lang], val))
            : val,
        obj
      );

    const updated = removeLanguageRecursively(userTranslations);
    setUserTranslations(updated);
    setLanguage("en");
  };

  const categories = R.groupBy(
    ([key]) => dict._categories?.[key] || "uncategorised",
    R.toPairs(R.omit(["_categories"], dict))
  );

  const isUserLanguage = (lang) =>
    Object.values(userTranslations || {}).some((cat) => cat?.language?.[lang]);

  const getMissingCountForLang = (lang) =>
    Object.entries(dict).reduce((count, [key, translations]) => {
      if (key === "_categories") return count;
      const value =
        editing[key]?.[lang] ??
        userTranslations?.[key]?.[lang] ??
        translations?.[lang];
      return value?.trim() ? count : count + 1;
    }, 0);

  return (
    <div className="translation-editor">
      <h2>{dict.language?.[language] || "Translation Editor"}</h2>
      <table>
        <thead>
          <tr>
            <th>🔑</th>
            {langs.map((lang) => {
              const isCurated = !!curatedLanguages[lang];
              const isISO = isoSet.has(lang);
              const isCustom = !isCurated && !isISO;
              const className = isCurated
                ? "lang-curated"
                : isISO
                  ? "lang-iso"
                  : "lang-custom";

              const label = dict.language?.[lang] || lang.toUpperCase();
              const labelType = isCurated
                ? STRINGS[L.LANG_CURATED]
                : isCustom
                  ? STRINGS[L.LANG_CUSTOM]
                  : STRINGS[L.LANG_STANDARD];
              const fullLabel = `${label} (${labelType})`;

              return (
                <th key={lang} title={fullLabel} aria-label={fullLabel}>
                  <div className="lang-header">
                    <span className={className}>{label}</span>
                    {getMissingCountForLang(lang) > 0 &&
                      !isCurated &&
                      (untranslatedFilter === null ||
                        untranslatedFilter === lang) && (
                        <CSButton
                          className="actionButton"
                          onClick={() =>
                            setUntranslatedFilter((prev) =>
                              prev === lang ? null : lang
                            )
                          }
                        >
                          {/* warning icon is presentational; can be moved to @cadenza/ui if you prefer */}
                          ⚠
                        </CSButton>
                      )}
                    {isUserLanguage(lang) && lang !== "en" && (
                      <div className="right-button-container">
                        <DeleteButton
                          onClick={() => handleRemoveLanguage(lang)}
                          aria-label={`Remove ${label}`}
                          title={`Remove ${label}`}
                          className="te-delete"
                        />
                      </div>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        {Object.entries(categories).map(([category, entries]) => {
          const isCollapsed = collapsedCategories[category];
          const isUntranslated = (key, lang) =>
            !(
              editing[key]?.[lang] ??
              userTranslations?.[key]?.[lang] ??
              dict?.[key]?.[lang]
            );

          return (
            <tbody key={category}>
              <tr
                className="category-header"
                onClick={() => toggleCategory(category)}
              >
                <td colSpan={langs.length + 1}>
                  <span className="category-toggle">
                    {isCollapsed ? "▶" : "▼"}
                  </span>{" "}
                  {STRINGS[CL[category.toUpperCase()]] || category}
                </td>
              </tr>
              {!isCollapsed &&
                entries.map(([key, translations]) => {
                  if (
                    untranslatedFilter &&
                    !isUntranslated(key, untranslatedFilter)
                  )
                    return null;

                  return (
                    <tr key={key}>
                      <td>{renderIconForKey(key)}</td>
                      {langs.map((lang) => {
                        const cat = dict._categories?.[key] || "uncategorised";
                        const val =
                          editing[cat]?.[key]?.[lang] ??
                          userTranslations?.[cat]?.[key]?.[lang] ??
                          translations?.[lang] ??
                          "";

                        return (
                          <td key={lang}>
                            <CSTextInput
                              value={val}
                              onChange={(e) =>
                                handleChange(key, lang, e.target.value)
                              }
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>
          );
        })}
      </table>

      <hr />
      <div className="button-container left-button-container small-buttons te-toolbar">
        <CSButton onClick={handleSave}>💾 {STRINGS[ACTIONS.SAVE]}</CSButton>
        <CSButton onClick={handleExport}>⬇ JSON</CSButton>
        <CSButton onClick={handleExportCSV}>⬇ CSV</CSButton>

        <label className="te-file-label">
          ⬆ JSON:
          <input
            className="te-file-input"
            type="file"
            onChange={handleImport}
            accept="application/json"
          />
        </label>

        <label className="te-file-label">
          ⬆ CSV:
          <input
            className="te-file-input"
            type="file"
            onChange={handleImportCSV}
            accept=".csv,text/csv"
          />
        </label>
      </div>

      <div className="language-adder">
        <label htmlFor="new-lang">Add Language:</label>
        <CSTextInput
          list="available-langs"
          id="new-lang"
          value={newLangCode}
          onChange={(e) => setNewLangCode(e.target.value)}
          placeholder="Type or select a code..."
        />
        <datalist id="available-langs">
          {unusedLangs.map(([code, label]) => (
            <option key={code} value={code}>
              {label} ({code})
            </option>
          ))}
        </datalist>
        <CSButton onClick={handleAddLanguage} disabled={!newLangCode}>
          ＋
        </CSButton>
      </div>
    </div>
  );
};

export default TranslationEditor;
